import React, { useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Switch,
  Grid,
  Snackbar,
  Alert,
  TableFooter,
  TablePagination,
} from "@mui/material";

import { withAdminAuth } from "../../HOC";
import {
  useGetAllUsersQuery,
  useToggleUserStatusMutation,
} from "../../Apis/authApi";

function Users() {
  // TablePagination uses ZERO-BASED index
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [loadingRow, setLoadingRow] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { data, isFetching, refetch } = useGetAllUsersQuery({
    page: page + 1, // API usually expects 1-based page
    rowsPerPage,
    search,
  });

  const [toggleUserStatus] = useToggleUserStatusMutation();

  const users = data?.result?.users || [];
  const pagination = data?.result?.pagination || {};

  const filteredUsers = users.filter((u: any) => {
    if (statusFilter === "active") return u.isActive;
    if (statusFilter === "inactive") return !u.isActive;
    return true;
  });

  const handleToggle = async (
    userId: string,
    currentStatus: boolean
  ) => {
    try {
      setLoadingRow(userId);

      await toggleUserStatus({
        userId,
        toggle: !currentStatus,
      }).unwrap();

      setSnackbar({
        open: true,
        message: `User ${
          !currentStatus ? "activated" : "deactivated"
        } successfully`,
        severity: "success",
      });

      refetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update user status",
        severity: "error",
      });
    } finally {
      setLoadingRow(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 4 }} >
          <TextField
            fullWidth
            label="Search by name, email, entity..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Status Filter</InputLabel>

            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">All Users</MenuItem>
              <MenuItem value="active">Active Only</MenuItem>
              <MenuItem value="inactive">Inactive Only</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Table */}
      {isFetching ? (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead
              sx={{
                backgroundColor: "rgba(81, 35, 132, 1)",
              }}
            >
              <TableRow>
                <TableCell
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  Full Name
                </TableCell>

                <TableCell
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  Email
                </TableCell>

                <TableCell
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  Phone
                </TableCell>
                <TableCell
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  Job
                </TableCell>
                <TableCell
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  Entity
                </TableCell>

                <TableCell
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  Status
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredUsers.map((user: any) => (
                <TableRow key={user.id}>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {user.fullName}
                  </TableCell>

                  <TableCell>{user.email}</TableCell>

                  <TableCell>{user.phoneNumber}</TableCell>
                  <TableCell>{user.job}</TableCell>

                  <TableCell>{user.entity}</TableCell>

                  <TableCell>
                    <Switch
                      checked={user.isActive}
                      disabled={loadingRow === user.id}
                      onChange={() =>
                        handleToggle(user.id, user.isActive)
                      }
                      color="success"
                    />
                  </TableCell>
                </TableRow>
              ))}

              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TablePagination
                  
                  rowsPerPageOptions={[5, 10, 25, 50, { label: 'All', value: -1 }]}
                  colSpan={5}
                  count={pagination?.totalCount || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withAdminAuth(Users);